export const MEASUREMENT_ID = 'G-DB5EPRKKW4';

export function publicPagePath(pathname: string): string {
  const path = String(pathname || '/').split('?')[0].split('#')[0] || '/';
  return path.replace(/^\/quote\/[^/]+/, '/quote');
}

export function shouldRecordAnalytics(pathname: string): boolean {
  const path = String(pathname || '/').split('?')[0].split('#')[0] || '/';
  return path !== '/admin' && !path.startsWith('/admin/') && !path.startsWith('/blog/preview');
}

export function recordEvent(name: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined' || !shouldRecordAnalytics(window.location.pathname)) return;
  const gtag = (window as Window & { gtag?: Gtag }).gtag;
  if (!gtag) return;
  gtag('event', name, params);
}

export type ContactMethod = 'whatsapp' | 'phone' | 'email';

const CONTACT_EVENTS: Record<ContactMethod, string> = {
  whatsapp: 'whatsapp_click',
  phone: 'phone_click',
  email: 'email_click',
};

export function recordContact(method: ContactMethod, source: string, extra: { link_url?: string } = {}): void {
  if (typeof window === 'undefined' || !shouldRecordAnalytics(window.location.pathname)) return;
  recordEvent(CONTACT_EVENTS[method], {
    source,
    page_path: publicPagePath(window.location.pathname),
    link_url: String(extra.link_url || ''),
  });
  recordEvent('generate_lead', { method, source });
}

export function recordQuoteFormSubmit(service: string, ok: boolean): void {
  if (!ok) return;
  const chosen = String(service || '').trim();
  recordEvent('quote_form_submit', { service: chosen, source: 'quote_page' });
  recordEvent('generate_lead', { method: 'quote_form' });
}

function contactMethod(href: string): ContactMethod | '' {
  const value = String(href || '').trim();
  if (/^tel:/i.test(value)) return 'phone';
  if (/^mailto:/i.test(value)) return 'email';
  if (/^whatsapp:/i.test(value) || /(?:^|\/\/|\.)wa\.me(?:\/|$)/i.test(value) || /api\.whatsapp\.com/i.test(value)) return 'whatsapp';
  return '';
}

function contactSource(anchor: Element): string {
  const marked = anchor.closest('[data-track-source]');
  const named = marked && marked.getAttribute('data-track-source');
  if (named) return named;
  if (anchor.closest('header, .navbar')) return 'header';
  if (anchor.closest('footer, .site-footer')) return 'footer';
  const path = publicPagePath(window.location.pathname);
  if (path === '/') return 'home';
  if (path === '/contact') return 'contact';
  if (path === '/quote') return 'quote_page';
  if (path.startsWith('/services')) return 'service';
  if (path.startsWith('/blog')) return 'blog';
  if (path.startsWith('/galleries')) return 'gallery_category';
  return 'page';
}

function onDocumentContactClick(event: Event): void {
  const target = event.target as Element | null;
  const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
  if (!anchor) return;
  const href = anchor.getAttribute('href') || '';
  const method = contactMethod(href);
  if (!method) return;
  recordContact(method, contactSource(anchor), { link_url: href });
}

let contactTrackingInstalled = false;

export function installContactTracking(): void {
  if (contactTrackingInstalled || typeof document === 'undefined') return;
  contactTrackingInstalled = true;
  document.addEventListener('click', onDocumentContactClick, true);
}

export function resetContactTrackingForTests(): void {
  if (typeof document !== 'undefined') {
    document.removeEventListener('click', onDocumentContactClick, true);
  }
  contactTrackingInstalled = false;
}

type Gtag = (...args: unknown[]) => void;

export function recordPageView(pathname: string, title = ''): void {
  if (!shouldRecordAnalytics(pathname)) return;
  const gtag = (window as Window & { gtag?: Gtag }).gtag;
  if (!gtag) return;
  const pagePath = publicPagePath(pathname);
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_title: title,
  });
}
